import React from 'react';
import { createRoot } from 'react-dom/client';

type MarketingSceneName = 'ai-transform' | 'days-phone' | 'space-phone' | 'ai-inline' | 'project-space';

function StatusBar() {
  return (
    <div className="story-status" aria-hidden="true">
      <time>12:42</time>
      <i className="story-dynamic-island" />
      <span className="story-status-icons"><i /><b /><em /></span>
    </div>
  );
}

function HomeIndicator() {
  return <i className="story-home-indicator" aria-hidden="true" />;
}

function CapturePhone({ src, label, className = '' }: { src: string; label: string; className?: string }) {
  return (
    <div className={`story-phone story-capture-phone ${className}`} data-screen-label={label} aria-hidden="true">
      <img src={src} alt="" loading="lazy" decoding="async" />
    </div>
  );
}

function ComposerPaperclipIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m16 6-8.414 8.586a2 2 0 0 0 2.829 2.829l8.414-8.586a4 4 0 1 0-5.657-5.657l-8.379 8.551a6 6 0 1 0 8.485 8.485l8.379-8.551" />
    </svg>
  );
}

function ComposerMicIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 19v3" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <rect x="9" y="2" width="6" height="13" rx="3" />
    </svg>
  );
}

function ComposerArrowUpIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m5 12 7-7 7 7" />
      <path d="M12 19V5" />
    </svg>
  );
}

function AiTransformScene() {
  return (
    <div className="story-illustration story-ai-phone-pair" aria-hidden="true">
      <CapturePhone
        src="/assets/product/oat2-puppy-prompt-screen.png"
        label="01 Ask Oat2 AI"
        className="story-puppy-prompt-phone"
      />
      <CapturePhone
        src="/assets/product/oat2-puppy-arrival-screen.png"
        label="02 Puppy Arrival Space"
        className="story-puppy-result-phone"
      />
      <div className="story-composer-popout">
        <p>Help us get ready for our new puppy.</p>
        <div className="story-composer-controls">
          <span className="story-composer-target story-composer-tool"><i><ComposerPaperclipIcon /></i></span>
          <span className="story-composer-spacer" />
          <span className="story-composer-target story-composer-voice"><i><ComposerMicIcon /></i></span>
          <span className="story-composer-target story-composer-send"><i><ComposerArrowUpIcon /></i></span>
        </div>
      </div>
    </div>
  );
}

function DaysPhoneScene() {
  return (
    <div className="story-illustration story-days-scene">
      <CapturePhone src="/assets/product/oat2-days-screen.webp" label="01 Days" />
    </div>
  );
}

function SpacePhoneScene() {
  return (
    <div className="story-illustration story-space-capture">
      <CapturePhone src="/assets/product/oat2-space-screen.webp" label="02 Space detail" />
    </div>
  );
}

function AiInlineScene() {
  return (
    <div className="story-illustration story-inline-ai">
      <div className="story-phone story-ui-phone" data-screen-label="03 Contextual AI suggestion" aria-hidden="true">
        <StatusBar />
        <div className="story-phone-head"><span>Friday</span><small>Aug 21</small></div>
        <div className="story-context-space"><img src="/assets/photography/big-sur-hero-v2.webp" alt="" /><div><small>THIS WEEKEND</small><strong>Big Sur weekend</strong><span>Starts tomorrow</span></div></div>
        <div className="story-today-row story-context-row"><time>4:00</time><p><strong>Pack cold-weather layers</strong><small>Big Sur weekend</small></p></div>
        <div className="story-agent-dock">
          <header><i /><span>Oat2 AI</span><small>Found in the weekend plan</small></header>
          <p>Bring the packing list into Friday so it is ready before you leave?</p>
          <div className="story-suggestion-preview"><i /><span><strong>Pack for Big Sur</strong><small>Friday · both of you</small></span></div>
          <div><button type="button" tabIndex={-1}>Leave it</button><button type="button" tabIndex={-1}>Add to Friday</button></div>
        </div>
        <HomeIndicator />
      </div>
    </div>
  );
}

function ProjectSpaceScene() {
  return (
    <div className="story-illustration story-project-space story-spaces-capture">
      <div className="hero-device story-spaces-device" data-screen-label="04 Spaces" aria-hidden="true">
        <img src="/assets/product/oat2-spaces-screen.png" alt="" loading="lazy" decoding="async" />
      </div>
    </div>
  );
}

const scenes: Record<MarketingSceneName, React.ComponentType> = {
  'ai-transform': AiTransformScene,
  'days-phone': DaysPhoneScene,
  'space-phone': SpacePhoneScene,
  'ai-inline': AiInlineScene,
  'project-space': ProjectSpaceScene,
};

document.querySelectorAll<HTMLElement>('[data-product-scene]').forEach((root) => {
  const name = root.dataset.productScene as MarketingSceneName | undefined;
  if (!name || !scenes[name]) return;
  const Scene = scenes[name];
  createRoot(root).render(<Scene />);
});
